import { useContext, useMemo, useState } from 'react';
import { faChartMixed, faMagnifyingGlass, faDownload } from '@fortawesome/pro-regular-svg-icons';
import { Avatar, CellBorderWidthEnum, FormSelectOption, ModularTable, Pagination, RowType, SortEnum, SwitchThemeContext, TableColorThemeEnum, TextField } from '@tryhackme/thm-ui-components';
import { ThemeColorMode } from 'src/common/enums';
import { ToolTip } from 'src/common/components';
import { useGetStatsScoreboardQuery } from '../../manage-room.slice';
import { ManageRoomDetail } from '../../manage-room.types';
import { StyledDefaultText, StyledDownloadButton, StyledDownloadIcon, StyledPaginationContainer, StyledPaginationRecordsContainer, StyledRecordDetailText, StyledRecordsWrapper, StyledSearchIcon, StyledSearchTextInputContainer, StyledStatsHeaderContainer, StyledStatsHeading, StyledStatsIcon, StyledStatsModularTableWrapper, StyledStatsOptionContainer, StyledUserAvatarContainer } from './stats-tab.styles';
import { StatsEmptyState, MemoizedStatsInfoCard } from './components';
import { statsPaginationOptions } from './stats-tab.constants';
import { useGetTransformedStats } from './hooks';
export const StatsTab = ({
  roomDetails
}: {
  roomDetails: ManageRoomDetail;
}) => {
  const roomCode = roomDetails?.code;
  const {
    theme
  } = useContext(SwitchThemeContext);
  const isDarkMode = theme === ThemeColorMode.DARK;
  const [paginationLimit, setPaginationLimit] = useState(statsPaginationOptions[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [activeSortColumn, setActiveSortColumn] = useState('rank');
  const [sortOrder, setSortOrder] = useState(SortEnum.ascending);
  const {
    data = [],
    isLoading
  } = useGetStatsScoreboardQuery(roomCode);
  const {
    transformedStatsData,
    csvData
  } = useGetTransformedStats(data);
  const showEmptyState = Boolean(!data?.length);
  const statsData = searchText ? transformedStatsData.filter(stat => stat.username.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())) : transformedStatsData;
  const limit = Number(paginationLimit?.value);
  const totalPages = Math.ceil(statsData.length / limit);
  const totalDocs = Number(statsData.length);
  const hasNextPage = currentPage < totalPages;
  const endOffset = currentPage * limit;
  const startOffset = endOffset - limit;
  const sortedStats = useMemo(() => {
    const dataToSort = [...statsData];
    const isSortOrderAscending = sortOrder === SortEnum.ascending;
    if (activeSortColumn === 'questionCompleteText') {
      return dataToSort.sort((a, b) => {
        const aSolvedQuestions = Number(a.questionCompleteText.split('/')[0]);
        const bSolvedQuestions = Number(b.questionCompleteText.split('/')[0]);
        if (isSortOrderAscending) {
          return aSolvedQuestions - bSolvedQuestions;
        }
        return bSolvedQuestions - aSolvedQuestions;
      });
    }
    if (activeSortColumn === 'score') {
      return dataToSort.sort((a, b) => isSortOrderAscending ? a.score - b.score : b.score - a.score);
    }
    if (activeSortColumn === 'username') {
      return dataToSort.sort((a, b) => {
        if (isSortOrderAscending) {
          if (a.username < b.username) return -1;
          if (a.username > b.username) return 1;
          return 0;
        }
        if (a.username > b.username) return -1;
        if (a.username < b.username) return 1;
        return 0;
      });
    }
    return dataToSort.sort((a, b) => isSortOrderAscending ? a.rank - b.rank : b.rank - a.rank);
  }, [activeSortColumn, sortOrder, statsData]);
  const paginatedStats = useMemo(() => {
    const chunkedUserStats = sortedStats.slice(startOffset, endOffset);
    return chunkedUserStats;
  }, [endOffset, sortedStats, startOffset]);
  const rows = useMemo(() => (paginatedStats?.map(item => ({
    id: item?.username,
    rank: <StyledDefaultText>{item?.rank}</StyledDefaultText>,
    username: <StyledUserAvatarContainer>
            <Avatar hasBorder={false} avatarSrc={item?.avatar} size="3.2rem" />
            <StyledDefaultText>{item?.username}</StyledDefaultText>
          </StyledUserAvatarContainer>,
    questionCompleteText: <StyledDefaultText>{item?.questionCompleteText}</StyledDefaultText>,
    score: <StyledDefaultText>{item?.score}</StyledDefaultText>
  })) as RowType[]), [paginatedStats]);
  const handleStatsPaginationLimit = (value?: FormSelectOption) => {
    setCurrentPage(1);
    setPaginationLimit(value);
  };
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  const handleSortFunction = (column: string) => {
    setActiveSortColumn(column);
    setSortOrder(sortOrder === SortEnum.ascending ? SortEnum.descending : SortEnum.ascending);
  };
  const downloadTaskCSV = () => {
    const csvContent = `data:text/csv;charset=utf-8,${csvData.map(e => e.join(',')).join('\n')}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${roomCode} - Task Progress.csv`);
    document.body.append(link);
    link.click();
  };
  return <section aria-label="Stats Tab" data-sentry-component="StatsTab" data-sentry-source-file="stats-tab.tsx">
      <StyledStatsHeaderContainer data-sentry-element="StyledStatsHeaderContainer" data-sentry-source-file="stats-tab.tsx">
        <StyledStatsIcon icon={faChartMixed} data-sentry-element="StyledStatsIcon" data-sentry-source-file="stats-tab.tsx" />
        <StyledStatsHeading data-sentry-element="StyledStatsHeading" data-sentry-source-file="stats-tab.tsx">Room Stats</StyledStatsHeading>
      </StyledStatsHeaderContainer>

      <MemoizedStatsInfoCard data-sentry-element="MemoizedStatsInfoCard" data-sentry-source-file="stats-tab.tsx" />

      <StyledStatsOptionContainer data-sentry-element="StyledStatsOptionContainer" data-sentry-source-file="stats-tab.tsx">
        <StyledSearchTextInputContainer data-sentry-element="StyledSearchTextInputContainer" data-sentry-source-file="stats-tab.tsx">
          <TextField variant="input" placeholder="Search for username" leftAdornment={<StyledSearchIcon isDarkMode={isDarkMode} icon={faMagnifyingGlass} />} onChange={event => setSearchText(event.target.value)} data-sentry-element="TextField" data-sentry-source-file="stats-tab.tsx" />
        </StyledSearchTextInputContainer>

        <StyledPaginationRecordsContainer data-sentry-element="StyledPaginationRecordsContainer" data-sentry-source-file="stats-tab.tsx">
          <ToolTip toolTipText="You can download all users progress in a spreadsheet; 
          in the sheet, T1Q1 represents task 1 question 1." data-sentry-element="ToolTip" data-sentry-source-file="stats-tab.tsx">
            <StyledDownloadButton variant="outlined" color="secondary" disabled={showEmptyState} onClick={() => downloadTaskCSV()} data-sentry-element="StyledDownloadButton" data-sentry-source-file="stats-tab.tsx">
              <StyledDownloadIcon icon={faDownload} data-sentry-element="StyledDownloadIcon" data-sentry-source-file="stats-tab.tsx" />
              Download task progress
            </StyledDownloadButton>
          </ToolTip>

          <StyledRecordsWrapper data-sentry-element="StyledRecordsWrapper" data-sentry-source-file="stats-tab.tsx">
            <TextField isDisabled={showEmptyState} isMulti={false} variant="select" selected={paginationLimit} options={statsPaginationOptions} isSearchable={false} handleSelection={value => handleStatsPaginationLimit(value)} leftOutsideAdornment={<StyledDefaultText>show</StyledDefaultText>} rightOutsideAdornment={<StyledDefaultText>records</StyledDefaultText>} data-sentry-element="TextField" data-sentry-source-file="stats-tab.tsx" />
          </StyledRecordsWrapper>
        </StyledPaginationRecordsContainer>
      </StyledStatsOptionContainer>

      <StyledStatsModularTableWrapper data-sentry-element="StyledStatsModularTableWrapper" data-sentry-source-file="stats-tab.tsx">
        <ModularTable isLoading={isLoading} activeSortColumn={showEmptyState ? null : activeSortColumn} activeSortOrder={sortOrder} cellBorderWidth={CellBorderWidthEnum.thick} colorTheme={TableColorThemeEnum.grayWhite} columns={[{
        key: 'rank',
        flex: '1',
        title: 'Rank in room',
        justifyContent: 'center',
        sortFunction: () => {
          handleSortFunction('rank');
        }
      }, {
        flex: '2',
        title: 'Username',
        key: 'username',
        sortFunction: () => {
          handleSortFunction('username');
        }
      }, {
        flex: '1.5',
        key: 'questionCompleteText',
        title: 'Questions Complete',
        sortFunction: () => {
          handleSortFunction('questionCompleteText');
        },
        justifyContent: 'center'
      }, {
        flex: '1',
        title: 'Points in room',
        justifyContent: 'center',
        key: 'score',
        sortFunction: () => {
          handleSortFunction('score');
        }
      }]} rows={rows} data-sentry-element="ModularTable" data-sentry-source-file="stats-tab.tsx" />
        {!isLoading && showEmptyState && <StatsEmptyState />}
      </StyledStatsModularTableWrapper>

      {!isLoading && totalPages > 0 && <StyledPaginationContainer>
          <StyledRecordDetailText>{`Showing ${startOffset + 1} to ${hasNextPage ? endOffset : totalDocs} of ${totalDocs} records`}</StyledRecordDetailText>
          <div>
            <Pagination totalPages={totalPages} selectedPage={currentPage} onChange={handlePageChange} />
          </div>
        </StyledPaginationContainer>}
    </section>;
};